import React from "react";
import { useTranslation } from "react-i18next";
import { PaymentsRoutesResponse } from "src/api";
import { Button, ErrorAlert, Field, InfoField, PaymentLimits } from "src/components";
import { useTrustPaymentsWithdrawal } from "src/hooks";
import { formatNumberToLocale } from "src/utils";
import TrustPaymentsWithdrawalCardForm from "./TrustPaymentsWithdrawalCardForm";
import "./trustpayments-withdrawal-form.scss";

// Two-step "Sell crypto → refund to card" UI:
//   1. Amount input → BE creates an `ACCOUNTCHECK` session.
//   2. SecureTrading SDK collects PAN, runs ACCOUNTCHECK, then BE
//      issues the real CFT REFUND with the returned tx ref.
const TrustPaymentsWithdrawalForm: React.FC<{
  route: PaymentsRoutesResponse;
}> = ({ route }): JSX.Element => {
  const { t } = useTranslation();
  const {
    sourceCurrency,
    targetCurrency,
    sourceCurrencyPrecision,
    targetCurrencyPrecision,
    amount,
    handleAmountChange,
    amountTouched,
    setAmountTouched,
    amountValid,
    amountPositive,
    insufficientBalance,
    belowMinimum,
    availableBalance,
    targetAmount,
    totpRequired,
    mfaToken,
    handleMfaTokenChange,
    mfaTouched,
    setMfaTouched,
    mfaValid,
    loading,
    error,
    sessionId,
    completed,
    submit,
    submitWithdrawalWithRef,
    submitDisabled,
    reset,
    platformFee,
    feeCurrency,
    quoteLoading,
    estimatedReceive,
    secondsUntilRefresh,
    fxEnabled,
    fxQuote,
    fxError,
    limitsStatus,
    availableLimit,
    limitsLoading,
    limitsCurrency,
    limitExceeded,
    belowMinLimit,
    minLimitAmount,
  } = useTrustPaymentsWithdrawal(route);

  if (!targetCurrency || !sourceCurrency) {
    return (
      <section className="trustpayments-withdrawal-form trustpayments-withdrawal-form__unavailable">
        <p className="trustpayments-withdrawal-form__unavailable-text">
          {t("TRUSTPAYMENTS_WITHDRAWAL.UNAVAILABLE", {
            defaultValue: "Card withdrawals are not configured for this exchange.",
          })}
        </p>
      </section>
    );
  }

  if (sessionId) {
    // Keep the SDK card form mounted into terminal state — Trust's
    // iframes (`#st-card-number` etc.) lose internal state if we
    // unmount them, which produced the "widget closes to white loader"
    // bug. The success / failed overlay renders as a sibling and the
    // card form's __shell is collapsed via CSS when `completed` is set.
    return (
      <section className="trustpayments-withdrawal-form trustpayments-withdrawal-form--card-step">
        <TrustPaymentsWithdrawalCardForm
          sessionId={sessionId}
          onReset={reset}
          targetAmount={targetAmount}
          sourceCurrency={sourceCurrency}
          targetCurrency={targetCurrency}
          sourceCurrencyPrecision={sourceCurrencyPrecision}
          targetCurrencyPrecision={targetCurrencyPrecision}
          estimatedReceive={estimatedReceive}
          submitWithdrawalWithRef={submitWithdrawalWithRef}
          isTerminal={completed !== null}
          fxQuote={fxQuote}
        />

        {completed?.status === "success" && (
          <div className="trustpayments-withdrawal-form__overlay trustpayments-withdrawal-form--success">
            <div className="trustpayments-withdrawal-form__success-badge" aria-hidden />
            <h2 className="trustpayments-withdrawal-form__title">
              {t("TRUSTPAYMENTS_WITHDRAWAL.SUCCESS_TITLE", {
                defaultValue: "Withdrawal initiated",
              })}
            </h2>
            <p className="trustpayments-withdrawal-form__text">
              {t("TRUSTPAYMENTS_WITHDRAWAL.SUCCESS_TEXT", {
                defaultValue:
                  "Your refund is on its way. Most cards see the funds within a few minutes; some banks take 1-3 business days.",
              })}
            </p>
          </div>
        )}

        {completed?.status === "error" && (
          <div className="trustpayments-withdrawal-form__overlay trustpayments-withdrawal-form--failed">
            <div className="trustpayments-withdrawal-form__failed-badge" aria-hidden />
            <h2 className="trustpayments-withdrawal-form__title">
              {t("TRUSTPAYMENTS_WITHDRAWAL.FAILED_TITLE", {
                defaultValue: "Withdrawal failed",
              })}
            </h2>
            <p className="trustpayments-withdrawal-form__text">
              {completed.message ||
                t("TRUSTPAYMENTS_WITHDRAWAL.FAILED_TEXT", {
                  defaultValue: "The refund was declined. Try a different card or contact support.",
                })}
            </p>
            <div className="trustpayments-withdrawal-form__failed-actions">
              <Button
                title={t("TRUSTPAYMENTS_WITHDRAWAL.TRY_AGAIN", {
                  defaultValue: "Try again",
                })}
                className="deposit-button"
                onClick={reset}
              />
            </div>
          </div>
        )}
      </section>
    );
  }

  let amountError: string | undefined;
  if (amountTouched && !amountPositive) {
    amountError = t("TRUSTPAYMENTS_WITHDRAWAL.AMOUNT_REQUIRED", {
      defaultValue: "Enter an amount greater than 0",
    });
  } else if (insufficientBalance) {
    amountError = t("TRUSTPAYMENTS_WITHDRAWAL.INSUFFICIENT_BALANCE", {
      balance: `${formatNumberToLocale(availableBalance, targetCurrencyPrecision)} ${targetCurrency}`,
      defaultValue: `Not enough balance — available ${formatNumberToLocale(availableBalance, targetCurrencyPrecision)} ${targetCurrency}`,
    });
  } else if (belowMinimum) {
    amountError = t("TRUSTPAYMENTS_WITHDRAWAL.BELOW_MINIMUM", {
      fee: `${formatNumberToLocale(platformFee, targetCurrencyPrecision)} ${targetCurrency}`,
      defaultValue: `Amount too low — must exceed platform fee of ${formatNumberToLocale(platformFee, targetCurrencyPrecision)} ${targetCurrency}`,
    });
  }
  const mfaError =
    totpRequired && mfaTouched && !mfaValid
      ? t("TRUSTPAYMENTS_WITHDRAWAL.MFA_REQUIRED", {
          defaultValue: "Enter the 6-digit authenticator code",
        })
      : undefined;
  const limitError = limitExceeded
    ? t("VALIDATION.LIMIT_EXCEEDED", {
        paymentType: "withdrawal",
        available: formatNumberToLocale(availableLimit, 2),
        currency: limitsCurrency,
        defaultValue: `Amount exceeds your available withdrawal limit (${formatNumberToLocale(availableLimit, 2)} ${limitsCurrency})`,
      })
    : null;
  const minLimitError = belowMinLimit
    ? t("VALIDATION.BELOW_MIN_LIMIT", {
        paymentType: "withdrawal",
        minimum: formatNumberToLocale(minLimitAmount, 2),
        currency: limitsCurrency,
        defaultValue: `Amount is below the minimum withdrawal (${formatNumberToLocale(minLimitAmount, 2)} ${limitsCurrency})`,
      })
    : null;

  return (
    <form
      className="trustpayments-withdrawal-form"
      onSubmit={(e) => {
        e.preventDefault();
        setAmountTouched(true);
        submit();
      }}
    >
      <Field
        label={t("TRUSTPAYMENTS_WITHDRAWAL.SELL_AMOUNT_IN", {
          currency: targetCurrency,
          defaultValue: `Amount in ${targetCurrency}`,
        })}
        value={amount}
        onChange={(value) => handleAmountChange(value)}
        placeholder="0.00"
        disabled={loading}
        error={amountError}
        onBlur={() => setAmountTouched(true)}
      />

      <InfoField
        className="info-field--muted"
        label={t("TRUSTPAYMENTS_WITHDRAWAL.AVAILABLE", {
          defaultValue: "Available",
        })}
        value={`${formatNumberToLocale(availableBalance, targetCurrencyPrecision)} ${targetCurrency}`}
      />

      <InfoField
        className="info-field--muted"
        label={t("TRUSTPAYMENTS_WITHDRAWAL.PLATFORM_FEE", {
          defaultValue: "Platform fee",
        })}
        value={`${formatNumberToLocale(platformFee, targetCurrencyPrecision)} ${feeCurrency || targetCurrency}`}
        isLoading={quoteLoading}
      />

      <InfoField
        label={
          fxQuote
            ? t("TRUSTPAYMENTS_WITHDRAWAL.YOU_RECEIVE_LOCAL", {
                currency: fxQuote.local_currency,
                defaultValue: `You will receive in ${fxQuote.local_currency}`,
              })
            : t("TRUSTPAYMENTS_WITHDRAWAL.YOU_RECEIVE", {
                defaultValue: "You will receive",
              })
        }
        value={
          fxQuote
            ? `${formatNumberToLocale(fxQuote.local_amount, 2)} ${fxQuote.local_currency}`
            : amountValid
              ? `${formatNumberToLocale(estimatedReceive, sourceCurrencyPrecision)} ${sourceCurrency}`
              : "—"
        }
        isLoading={quoteLoading}
      />

      {amountValid && estimatedReceive > 0 && (
        <p className="trustpayments-withdrawal-form__quote-refresh">
          {quoteLoading
            ? t("TRUSTPAYMENTS_WITHDRAWAL.QUOTE_REFRESHING", {
                defaultValue: "Refreshing price…",
              })
            : t("TRUSTPAYMENTS_WITHDRAWAL.QUOTE_REFRESHES_IN", {
                seconds: secondsUntilRefresh,
                defaultValue: `Price refreshes in ${secondsUntilRefresh}s`,
              })}
        </p>
      )}

      {totpRequired && (
        <Field
          label={t("TRUSTPAYMENTS_WITHDRAWAL.MFA_CODE", {
            defaultValue: "Authenticator code",
          })}
          value={mfaToken}
          onChange={handleMfaTokenChange}
          placeholder="000000"
          disabled={loading}
          error={mfaError}
          onBlur={() => setMfaTouched(true)}
          maxLength={6}
        />
      )}

      <PaymentLimits
        limitsStatus={limitsStatus}
        availableLimit={availableLimit}
        currency={limitsCurrency}
        isLoading={limitsLoading}
      />

      <p className="trustpayments-withdrawal-form__disclaimer">
        {t("TRUSTPAYMENTS_WITHDRAWAL.PAYOUT_TIMING", {
          defaultValue: "Refunds typically reach the cardholder within a few minutes; some banks may take 1-3 business days.",
        })}
      </p>

      <ErrorAlert
        message={
          error ||
          minLimitError ||
          limitError ||
          (fxEnabled && fxError
            ? t("TRUSTPAYMENTS_WITHDRAWAL.FX_QUOTE_UNAVAILABLE", {
                defaultValue:
                  "Couldn't load your local rate. Please try again in a moment.",
              })
            : null)
        }
      />

      <Button
        title={t("TRUSTPAYMENTS_WITHDRAWAL.CONTINUE", {
          defaultValue: "Continue",
        })}
        className="deposit-button"
        type="submit"
        disabled={submitDisabled}
        pending={loading}
      />
    </form>
  );
};

export default TrustPaymentsWithdrawalForm;
